import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardPool } from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { rechargeCardPoolApi } from '../../../redux/slices/CardGroupSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';

interface RechargeGroupModalProperties {
  group: CardPool;
  onClose: () => void;
  onSuccess?: (amountKwh?: number) => void;
}

/**
 * Safely extracts numeric balance in kWh from decimal object, number, or string
 * @param {any} balanceKwh - Raw energy balance value
 * @returns {number} Formatted numeric balance in kWh
 */
const getNumericBalance = (balanceKwh: any): number => {
  if (typeof balanceKwh === 'object' && balanceKwh !== null && '$numberDecimal' in balanceKwh) {
    return Number.parseFloat(balanceKwh.$numberDecimal) || 0;
  }
  if (typeof balanceKwh === 'number') {
    return balanceKwh;
  }
  if (typeof balanceKwh === 'string') {
    return Number.parseFloat(balanceKwh) || 0;
  }
  return 0;
};

/**
 * Modal form component to recharge energy balance on a Card Group
 * @param {RechargeGroupModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Recharge group form modal content
 */
function RechargeGroupModal(properties: RechargeGroupModalProperties): React.JSX.Element {
  const { group, onClose, onSuccess } = properties;
  const { t } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const { actionLoading } = useAppSelector((state) => state.cardGroups);

  const [amountKwh, setAmountKwh] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  /**
   * Handles recharge submission with validation
   * @returns {Promise<void>} Resolves when recharge finishes
   */
  const handleRecharge = async (): Promise<void> => {
    const amount = Number(amountKwh);
    if (!amountKwh || amount <= 0 || Number.isNaN(amount)) {
      const validationMessage = t('positiveAmountRequired');
      setErrorMessage(validationMessage);
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          code: 400,
          message: validationMessage,
        }),
      );
      return;
    }

    try {
      await dispatch(
        rechargeCardPoolApi({
          cardPoolId: group.id || group._id || '',
          amountKwh: amount,
        }),
      ).unwrap();

      if (onSuccess) {
        onSuccess(amount);
      } else {
        onClose();
      }
    } catch {
      // Error notification toast is dispatched automatically by rechargeCardPoolApi
    }
  };

  const currentBalance = getNumericBalance(group.balanceKwh);
  const creditAmount = Number(amountKwh) || 0;
  const estimatedNewBalance = (currentBalance + creditAmount).toFixed(2);

  return (
    <div className="rfid-modal-form">
      <div className="rfid-modal-form__body">
        <div className="rfid-recharge-summary-box">
          <div className="rfid-recharge-summary-box__item">
            <span className="rfid-recharge-summary-box__label">
              {group.kind === 'INDIVIDUAL'
                ? t('rfidCard')
                : t('cardGroup')}
            </span>
            <strong className="rfid-recharge-summary-box__value">
              {group.name || group.id || (group.kind === 'INDIVIDUAL' ? t('individualCard') : t('cardGroupValue'))} ({group.kind === 'INDIVIDUAL' ? t('individual') : t('group')})
            </strong>
          </div>
          <div className="rfid-recharge-summary-box__item">
            <span className="rfid-recharge-summary-box__label">{t('currentBalance')}</span>
            <strong className="rfid-recharge-summary-box__value numeric-unit" dir="ltr">
              {currentBalance.toFixed(2)} kWh
            </strong>
          </div>
          <div className="rfid-recharge-summary-box__item rfid-recharge-summary-box__item--projected">
            <span className="rfid-recharge-summary-box__label">{t('projectedBalance')}</span>
            <strong className="rfid-recharge-summary-box__value numeric-unit" dir="ltr">
              {estimatedNewBalance} kWh
            </strong>
          </div>
        </div>

        <div className="rfid-modal-form__field">
          <CustomInput
            inputLabel={t('rechargeAmount')}
            type="number"
            name="amountKwh"
            placeholder={t('eg250')}
            value={amountKwh}
            valueRequired
            error={Boolean(errorMessage)}
            errorMessage={errorMessage}
            onChangeValue={(_, value) => {
              setAmountKwh(String(value));
              if (errorMessage) {
                setErrorMessage(undefined);
              }
            }}
          />
        </div>
      </div>

      <div className="rfid-modal-form__actions">
        <MyButton
          label={t('cancel')}
          buttonType="outline"
          onClickFunc={onClose}
        />
        <MyButton
          label={
            actionLoading
              ? t('processing')
              : t('creditBalance')
          }
          buttonType="primary"
          onClickFunc={handleRecharge}
          disableButton={actionLoading}
        />
      </div>
    </div>
  );
}

export default RechargeGroupModal;
